import moment from "moment";
import { ApiGeoPoint, BookingStatus } from "../../Services/BookingEntities";
import { Dispatch } from "../Dispatch";
import { GetGoogleDirections } from "../MyBookings/ETA/GetGoogleDirections";
import { OptimismMultiplier } from "../MyBookings/ETA/Parameters";
import { ExtendedEta, EtaKind } from "../MyBookings/MyBookingEntities";
import { PhoneToCnpBookingDetails } from "./PhoneToCnpEntities";

/** Compute the ETA of the specified booking using Google Directions API.
 *  Then push the data into Redux.
 *  In this function, the booking record is assumed valid, e.g. vehicle location known, etc. 
 *  This will have been checked previously by e.g. DoesEtaNeedRefresh.
 *  As a result, there will be a few non-null type assertions in the code. 
 *  Returns the newly loaded ETA, if it is successful.
 * */
export async function PerformEtaRefresh(booking: PhoneToCnpBookingDetails): Promise<ExtendedEta | null> {

    // either dropoff or pickup
    let targetLocation: ApiGeoPoint;
    let etaKind: EtaKind;

    const vehicleLocation = booking.TrackingInfo.VehicleLocation!;

    if (booking.TrackingInfo.BookingStatusID == BookingStatus.Accepted) {
        targetLocation = {
            Latitude: booking.Pickup.Latitude,
            Longitude: booking.Pickup.Longitude
        };
        etaKind = EtaKind.ToPickup;
    }
    else {
        targetLocation = {
            Latitude: booking.DropOff.Latitude,
            Longitude: booking.DropOff.Longitude
        };
        etaKind = EtaKind.ToDropoff;
    }

    const directions = await GetGoogleDirections(vehicleLocation, targetLocation, booking.GoogleOdrdTripId);

    if (!directions) return null; // no further action possible

    // assume only one route and one leg, based on simple request
    const leg = directions.routes[0].legs[0];

    const TimeOfCalculation = moment();
    const BestGuessEtaSeconds = leg.duration!.value;
    const OptimisticEtaSeconds = BestGuessEtaSeconds * OptimismMultiplier;
    const EtaDisplayText = ComputeEtaDisplayText(OptimisticEtaSeconds);

    const eta: ExtendedEta = {
        BookingId: booking.BookingId,
        BestGuessEtaSeconds,
        EtaDisplayText,
        Kind: etaKind,
        OptimisticEtaSeconds,
        TimeOfCalculation,
        VehicleLocation: vehicleLocation,
    };

    Dispatch.PhoneToCnp.RefreshEta(eta);
    return eta;
}

/** Human readable display text for ETA.
 *  Round up to whole minutes.
 *  e.g. "1 min" or "2 mins". */
function ComputeEtaDisplayText(seconds: number) {

    const mins = Math.ceil(seconds / 60);
    const units = mins == 1 ? "min" : "mins";
    return `${mins} ${units}`;
}